import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaSave, FaSpinner, FaWrench } from 'react-icons/fa';

const apiDomain = process.env.REACT_APP_API_DOMAIN;
const apiBase = `${apiDomain}/api/v2/appsettings`;

const defaultBundles = [
  { coins: 50, priceINR: 50, label: '50 Coins', popular: false },
  { coins: 100, priceINR: 100, label: '100 Coins', popular: true },
  { coins: 200, priceINR: 200, label: '200 Coins', popular: false },
  { coins: 500, priceINR: 500, label: '500 Coins', popular: false },
  { coins: 1000, priceINR: 1000, label: '1000 Coins', popular: false },
];

const AppSettings = () => {
  const [identityProof1Label, setIdentityProof1Label] = useState('');
  const [identityProof2Label, setIdentityProof2Label] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [coinRateINR, setCoinRateINR] = useState(1.0);
  const [coinBundles, setCoinBundles] = useState(defaultBundles);
  const [newCoins, setNewCoins] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newPopular, setNewPopular] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ message: '', type: '' });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBase}/get`);
      const data = response.data;
      setIdentityProof1Label(data.identityProof1Label || '');
      setIdentityProof2Label(data.identityProof2Label || '');
      setMaintenanceMode(data.maintenanceMode || false);
      setMaintenanceMessage(data.maintenanceMessage || '');
      setCoinRateINR(data.coinRateINR || 1.0);
      setCoinBundles(data.coinBundles && data.coinBundles.length > 0 ? data.coinBundles : defaultBundles);
    } catch (error) {
      showAlert('Error fetching settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert({ message: '', type: '' }), 4000);
  };

  const handleAddBundle = () => {
    const coinsNum = parseInt(newCoins, 10);
    const priceNum = parseFloat(newPrice);
    if (isNaN(coinsNum) || coinsNum <= 0) {
      showAlert('Please enter a valid coin amount', 'error');
      return;
    }
    if (isNaN(priceNum) || priceNum <= 0) {
      showAlert('Please enter a valid price in ₹', 'error');
      return;
    }
    const bundle = {
      coins: coinsNum,
      priceINR: priceNum,
      label: newLabel.trim() || `${coinsNum} Coins (₹${priceNum})`,
      popular: newPopular,
    };
    setCoinBundles([...coinBundles, bundle]);
    setNewCoins('');
    setNewPrice('');
    setNewLabel('');
    setNewPopular(false);
    showAlert('Coin bundle added! Click Save to apply.', 'info');
  };

  const handleRemoveBundle = (index) => {
    const updated = coinBundles.filter((_, i) => i !== index);
    setCoinBundles(updated);
  };

  const handleSave = async () => {
    if (!identityProof1Label.trim() || !identityProof2Label.trim()) {
      showAlert('Both identity proof labels are required', 'error');
      return;
    }

    const rate = parseFloat(coinRateINR) || 1.0;
    const updatedBundles = coinBundles.map(b => ({
      ...b,
      priceINR: b.coins * rate,
      label: `${b.coins} Coins (₹${b.coins * rate})`
    }));

    setSaving(true);
    try {
      await axios.post(`${apiBase}/update`, {
        identityProof1Label: identityProof1Label.trim(),
        identityProof2Label: identityProof2Label.trim(),
        maintenanceMode,
        maintenanceMessage: maintenanceMessage.trim(),
        coinRateINR: rate,
        coinBundles: updatedBundles,
      }, {
        headers: {
          'X-API-Key': process.env.REACT_APP_MAINTENANCE_API_KEY || 'namasvi_maintenance_secret_key_2026'
        }
      });
      setCoinBundles(updatedBundles);
      showAlert('Settings & Coin Bundles saved successfully!', 'success');
    } catch (error) {
      showAlert('Error saving settings: ' + (error.response?.data || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setIdentityProof1Label('Identity Proof 1 (e.g., Aadhaar / Passport / National ID)');
    setIdentityProof2Label("Identity Proof 2 (e.g., Driver's License / Visa / Residence Permit)");
    setMaintenanceMode(false);
    setMaintenanceMessage('');
    showAlert('Labels and maintenance status reset. Click Save to apply.', 'info');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:mr-[2vw]">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
          App Settings
        </h1>
        <p className="text-center text-gray-500 mb-8">
          Customize field labels and toggle system maintenance status
        </p>

        {alert.message && (
          <div
            className={`${
              alert.type === 'success'
                ? 'bg-green-100 text-green-800 border-green-400'
                : alert.type === 'error'
                ? 'bg-red-100 text-red-800 border-red-400'
                : 'bg-blue-100 text-blue-800 border-blue-400'
            } border-l-4 p-4 mb-6 rounded-md`}
          >
            {alert.message}
          </div>
        )}

        {/* Identity Proof Labels Section */}
        <div className="bg-white shadow-lg rounded-xl p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-xl">📄</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Identity Proof Field Labels
              </h2>
              <p className="text-sm text-gray-500">
                These labels will be displayed to sponsors when uploading identity documents
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Identity Proof 1 Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Identity Proof 1 Label
              </label>
              <input
                type="text"
                value={identityProof1Label}
                onChange={(e) => setIdentityProof1Label(e.target.value)}
                placeholder="e.g., Aadhaar Card / Passport / National ID"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Typically used for Indian nationals (Aadhaar, PAN, Passport, etc.)
              </p>
            </div>

            {/* Identity Proof 2 Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Identity Proof 2 Label
              </label>
              <input
                type="text"
                value={identityProof2Label}
                onChange={(e) => setIdentityProof2Label(e.target.value)}
                placeholder="e.g., Driver's License / Visa / Residence Permit"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <p className="mt-1 text-xs text-gray-400">
                Typically used for international sponsors (Visa, Residence Permit, etc.)
              </p>
            </div>
          </div>

          {/* Preview Section */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 mb-3">Preview (How it appears in the app)</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">📋</span>
                <span className="text-sm text-gray-700 font-medium">{identityProof1Label || 'Identity Proof 1'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">📋</span>
                <span className="text-sm text-gray-700 font-medium">{identityProof2Label || 'Identity Proof 2'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Namasvi Coins & Top-Up Bundles Section */}
        <div className="bg-white shadow-lg rounded-xl p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center text-amber-600">
              <span className="text-xl">🪙</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Namasvi Coin Rate & Top-Up Bundles
              </h2>
              <p className="text-sm text-gray-500">
                Define the value of 1 Namasvi Coin and create dynamic coin top-up bundle options for users
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Base Coin Value */}
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <label className="block text-sm font-semibold text-amber-900 mb-1">
                Base Coin Value: 1 Coin = ₹ (INR)
              </label>
              <div className="flex items-center gap-3">
                <span className="text-sm text-amber-800 font-medium">1 Coin = ₹</span>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={coinRateINR}
                  onChange={(e) => setCoinRateINR(e.target.value)}
                  placeholder="1.0"
                  className="w-32 p-2 border border-amber-300 rounded-lg bg-white font-bold text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-500 text-center"
                />
              </div>
              <p className="mt-2 text-xs text-amber-700">
                e.g., if set to 5, then 1 Coin = ₹5. Custom coin top-ups and payment coin deductions will automatically calculate based on this rate.
              </p>
            </div>

            {/* Coin Bundles Management */}
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-3">Top-Up Coin Bundles List</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {coinBundles.map((b, idx) => (
                  <div key={idx} className="relative p-4 rounded-xl border border-gray-200 bg-gray-50 hover:border-amber-400 transition-all flex flex-col justify-between">
                    {b.popular && (
                      <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">
                        POPULAR
                      </span>
                    )}
                    <div>
                      <div className="text-lg font-bold text-gray-800">{b.coins} Coins</div>
                      <div className="text-sm text-amber-600 font-semibold">₹{b.coins * (parseFloat(coinRateINR) || 1)}</div>
                      <div className="text-xs text-gray-400 mt-1">{b.coins} Coins (₹{b.coins * (parseFloat(coinRateINR) || 1)})</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveBundle(idx)}
                      className="mt-3 text-xs text-red-500 hover:text-red-700 font-medium self-end"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              {/* Add New Bundle Form */}
              <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Add New Coin Bundle</h4>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Coins Amount</label>
                    <input
                      type="number"
                      value={newCoins}
                      onChange={(e) => setNewCoins(e.target.value)}
                      placeholder="e.g. 100"
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Price in ₹ (INR)</label>
                    <input
                      type="number"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      placeholder="e.g. 500"
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Label (Optional)</label>
                    <input
                      type="text"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      placeholder="e.g. Starter Pack"
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newPopular}
                        onChange={(e) => setNewPopular(e.target.checked)}
                        className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                      />
                      Popular Badge
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleAddBundle}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 transition-all"
                >
                  + Add Bundle
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* System Maintenance Section */}
        <div className="bg-white shadow-lg rounded-xl p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
              <FaWrench className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                System Maintenance Mode
              </h2>
              <p className="text-sm text-gray-500">
                Put the Namasvi platform (API & client apps) offline during major updates
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div>
                <span className="text-sm font-semibold text-slate-700 block">Maintenance Status</span>
                <span className="text-xs text-slate-500">Enable to redirect users and block API write requests.</span>
              </div>
              <button
                type="button"
                onClick={() => setMaintenanceMode(!maintenanceMode)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  maintenanceMode ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    maintenanceMode ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Maintenance Message (Optional)
              </label>
              <textarea
                rows={3}
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="e.g. We are performing scheduled database improvements. The system will be online in 3 to 4 hours."
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
              />
              <p className="mt-1 text-xs text-gray-400">
                This notice will display on the screen when sponsors and users open the apps.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 justify-center">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all"
          >
            {saving ? (
              <>
                <FaSpinner className="animate-spin" /> Saving...
              </>
            ) : (
              <>
                <FaSave /> Save Settings
              </>
            )}
          </button>
          <button
            onClick={handleReset}
            className="px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all"
          >
            Reset to Defaults
          </button>
        </div>

        {/* Info Card - Hidden for now (managed via API/cURL) */}
        {/*
        <div className="mt-8 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
          <h3 className="text-sm font-semibold text-indigo-800 mb-2">🛠️ Maintenance Operations Guide</h3>
          <ul className="text-sm text-indigo-700 space-y-1">
            <li>• Activating maintenance mode intercepts incoming calls at the Go API layer.</li>
            <li>• Safe exception filters allow admins to configure settings and view logs even during updates.</li>
            <li>• RTDB node <code>appSettings/maintenanceMode</code> holds the master flag checked by Flutter apps.</li>
          </ul>
        </div>
        */}
      </div>
    </div>
  );
};

export default AppSettings;
